import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Loader2, Car } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { createDevice, fetchGroups, type Device } from '@/lib/traccar-api';
import { VEHICLE_BRANDS, CATEGORY_TO_VEHICLE_TYPE, get3DIconUrl, ROTATION_FRAMES, DEVICE_CATEGORIES } from '@/data/car-brands';

const EMPTY_FORM = {
  name: '',
  groupId: 0,
  category: 'car',
  make: '',
  model: '',
  year: '',
  modelYear: '',
  color: '',
  licensePlate: '',
  vin: '',
  renavan: '',
  activationDate: '',
  notes: '',
};

interface AddVehicleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (device: Device) => void;
}

export function AddVehicleDialog({ open, onOpenChange, onCreated }: AddVehicleDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: groups = [] } = useQuery({
    queryKey: ['groups'],
    queryFn: fetchGroups,
    staleTime: 60000,
  });

  const [makeOpen, setMakeOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [makeSearch, setMakeSearch] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [rotationFrame, setRotationFrame] = useState(0);

  useEffect(() => {
    const interval = setInterval(
        () => setRotationFrame((rotationFrame) => (rotationFrame + ROTATION_FRAMES)%360),
        500
    )
    return () => clearInterval(interval);
  }, []);


  const vehicleType = CATEGORY_TO_VEHICLE_TYPE[formData.category] ?? null;

  const filteredBrands = useMemo(
    () => vehicleType ? VEHICLE_BRANDS.filter((b) => b.type === vehicleType) : VEHICLE_BRANDS,
    [vehicleType],
  );

  const brandNames = useMemo(() => filteredBrands.map((b) => b.brand), [filteredBrands]);

  const modelsForBrand = useMemo(() => {
    const entry = VEHICLE_BRANDS.find((b) => b.brand.toLowerCase() === formData.make.toLowerCase());
    return entry?.models ?? [];
  }, [formData.make]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) setFormData(EMPTY_FORM);
    onOpenChange(isOpen);
  };

  const mutation = useMutation({
    mutationFn: createDevice,
    onSuccess: (device) => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      toast.success(t('devices.created'));
      onCreated?.(device);
      handleOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const attributes: Record<string, string | number | boolean> = {};
    if (formData.make) attributes.make = formData.make;
    if (formData.model) attributes.vehicleModel = formData.model;
    if (formData.year) attributes.year = formData.year;
    if (formData.modelYear) attributes.modelYear = formData.modelYear;
    if (formData.color) attributes.color = formData.color;
    if (formData.licensePlate) attributes.licensePlate = formData.licensePlate;
    if (formData.vin) attributes.vin = formData.vin;
    if (formData.renavan) attributes.renavan = formData.renavan;
    if (formData.activationDate) attributes.activationDate = formData.activationDate;
    if (formData.notes) attributes.notes = formData.notes;

    const uniqueId = `not_installed_${crypto.randomUUID()}`;

    mutation.mutate({
      name: formData.name,
      uniqueId,
      groupId: formData.groupId || undefined,
      category: formData.category,
      ...(Object.keys(attributes).length > 0 && { attributes }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="h-5 w-5" />
              {t('reseller.vehicle.add')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4 overflow-y-auto p-2 flex-1 min-h-0">
            <div className="grid gap-2">
              <Label htmlFor="add-device-name">{t('devices.name')}</Label>
              <Input
                id="add-device-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('devices.namePlaceholder')}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="add-device-group">{t('devices.group')}</Label>
                <Select
                  value={formData.groupId ? String(formData.groupId) : 'none'}
                  onValueChange={(value) => setFormData({ ...formData, groupId: value === 'none' ? 0 : Number(value) })}
                >
                  <SelectTrigger id="add-device-group">
                    <SelectValue placeholder={t('devices.selectGroup')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('devices.noGroup')}</SelectItem>
                    {groups.map((g) => (
                      <SelectItem key={g.id} value={String(g.id)}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="add-device-category">{t('devices.category')}</Label>
                <Select
                  value={formData.category || 'default'}
                  onValueChange={(value) => {
                    setFormData({ ...formData, category: value });
                  }}
                >
                  <SelectTrigger id="add-device-category">
                    <div className="flex items-center gap-2">
                      <img src={get3DIconUrl(formData.category, rotationFrame, '#22c55e')} alt="" className="h-12 w-12 object-contain"/>
                      <span>{t(`devices.category_${formData.category}`)}</span>
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {[...DEVICE_CATEGORIES]
                      .sort((a, b) =>
                        t(`devices.category_${a}`).localeCompare(t(`devices.category_${b}`))
                      )
                      .map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          <div className="items-center gap-2">
                            <img src={get3DIconUrl(cat, 45, '#22c55e')} alt=""  />
                            <span></span>
                            {t(`devices.category_${cat}`)}
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t pt-4 mt-1">
              <p className="text-sm font-medium text-muted-foreground mb-3">{t('devices.vehicleDetails')}</p>
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>{t('devices.make')}</Label>
                    <Popover open={makeOpen} onOpenChange={setMakeOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" aria-expanded={makeOpen} className="justify-between font-normal">
                          <span className="truncate">{formData.make || t('devices.makePlaceholder')}</span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput placeholder={t('devices.makePlaceholder')} value={makeSearch} onValueChange={setMakeSearch} />
                          <CommandList>
                            <CommandEmpty>
                              {makeSearch ? (
                                <button
                                  type="button"
                                  className="w-full text-sm cursor-pointer hover:underline"
                                  onClick={() => {
                                    setFormData({ ...formData, make: makeSearch, model: '' });
                                    setMakeSearch('');
                                    setMakeOpen(false);
                                  }}
                                >
                                  {t('devices.useCustomValue', { value: makeSearch })}
                                </button>
                              ) : t('devices.noResults')}
                            </CommandEmpty>
                            <CommandGroup>
                              {brandNames.map((brand) => (
                                <CommandItem
                                  key={brand}
                                  value={brand}
                                  onSelect={(val) => {
                                    const selected = VEHICLE_BRANDS.find((b) => b.brand.toLowerCase() === val.toLowerCase());
                                    setFormData({ ...formData, make: selected?.brand ?? val, model: '' });
                                    setMakeSearch('');
                                    setMakeOpen(false);
                                  }}
                                >
                                  <Check className={cn('mr-2 h-4 w-4', formData.make.toLowerCase() === brand.toLowerCase() ? 'opacity-100' : 'opacity-0')} />
                                  {brand}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="grid gap-2">
                    <Label>{t('devices.model')}</Label>
                    <Popover open={modelOpen} onOpenChange={setModelOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" aria-expanded={modelOpen} className="justify-between font-normal">
                          <span className="truncate">{formData.model || t('devices.modelPlaceholder')}</span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput placeholder={t('devices.modelPlaceholder')} value={modelSearch} onValueChange={setModelSearch} />
                          <CommandList>
                            <CommandEmpty>{t('devices.noResults')}</CommandEmpty>
                            {modelSearch && !modelsForBrand.some((m) => m.toLowerCase() === modelSearch.toLowerCase()) && (
                              <CommandGroup>
                                <CommandItem
                                  value={`__custom__${modelSearch}`}
                                  onSelect={() => {
                                    setFormData({ ...formData, model: modelSearch });
                                    setModelSearch('');
                                    setModelOpen(false);
                                  }}
                                >
                                  {t('devices.useCustomValue', { value: modelSearch })}
                                </CommandItem>
                              </CommandGroup>
                            )}
                            <CommandGroup>
                              {modelsForBrand.map((model) => (
                                <CommandItem
                                  key={model}
                                  value={model}
                                  onSelect={(val) => {
                                    const selected = modelsForBrand.find((m) => m.toLowerCase() === val.toLowerCase());
                                    setFormData({ ...formData, model: selected ?? val });
                                    setModelSearch('');
                                    setModelOpen(false);
                                  }}
                                >
                                  <Check className={cn('mr-2 h-4 w-4', formData.model.toLowerCase() === model.toLowerCase() ? 'opacity-100' : 'opacity-0')} />
                                  {model}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="add-vehicle-year">{t('devices.year')}</Label>
                    <Input id="add-vehicle-year" value={formData.year} onChange={(e) => setFormData({ ...formData, year: e.target.value })} placeholder={t('devices.yearPlaceholder')} />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="add-vehicle-model-year">{t('devices.modelYear')}</Label>
                    <Input id="add-vehicle-model-year" value={formData.modelYear} onChange={(e) => setFormData({ ...formData, modelYear: e.target.value })} placeholder={t('devices.yearPlaceholder')} />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="add-vehicle-color">{t('devices.color')}</Label>
                    <Input id="add-vehicle-color" value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} placeholder={t('devices.colorPlaceholder')} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="add-vehicle-plate">{t('devices.licensePlate')}</Label>
                    <Input id="add-vehicle-plate" value={formData.licensePlate} onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value })} placeholder={t('devices.licensePlatePlaceholder')} />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="add-vehicle-vin">{t('devices.vin')}</Label>
                    <Input id="add-vehicle-vin" value={formData.vin} onChange={(e) => setFormData({ ...formData, vin: e.target.value })} placeholder={t('devices.vinPlaceholder')} />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="add-vehicle-renavan">{t('devices.renavan')}</Label>
                    <Input id="add-vehicle-renavan" value={formData.renavan} onChange={(e) => setFormData({ ...formData, renavan: e.target.value })} />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="add-vehicle-activation-date">{t('devices.activationDate')}</Label>
                    <Input
                      id="add-vehicle-activation-date"
                      type="date"
                      value={formData.activationDate}
                      onChange={(e) => setFormData({ ...formData, activationDate: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="add-vehicle-notes">{t('common.notes')}</Label>
                  <Textarea id="add-vehicle-notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={mutation.isPending}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
